// ./src/components/features/Tasks/TasksList/TasksList.tsx
//
// Event tasks / appointments list — table on desktop (>= md), cards on tablet/mobile.
// Tap-safe (explicit Edit/Delete only), rights-gated per row, selection-ready. Each
// row carries the TaskProgress control (status + tracked time + start/pause/complete/
// reopen). Category name + icon/color are resolved from the shared CategoriesContext.
// Mirrors PoisList (see em-scaffold-ui).

import React, { useCallback, useMemo } from "react";
import {
  Paper,
  Stack,
  Group,
  Text,
  Box,
  Badge,
  ThemeIcon,
  ActionIcon,
  Tooltip,
  Skeleton,
  Table,
  Checkbox,
  Anchor,
} from "@mantine/core";
import { motion, AnimatePresence } from "framer-motion";
import { STATUSES } from "@sdflc/utils";

import { Task, TaskTransitionAction } from "@/utils/api";
import { RowSelection } from "@/hooks";
import { useAppEnvironment } from "@/contexts";
import { useOptionalTasksContext } from "@/contexts/TasksContext";
import { useOptionalCategoriesContext } from "@/contexts/CategoriesContext";
import {
  AppIcon,
  EmptyState,
  SwipeableCard,
  SwipeAction,
  useSwipeableList,
  LoadMoreButton,
  CardSkeletonActions,
  CardSkeletonLines,
  CardSkeletonCheckbox,
  countCardActions,
  type CardSkeletonShape,
} from "@/components/generic";
import {
  useEntranceAnimation,
  entranceListVariants,
  entranceCardVariants,
} from "@/hooks/useEntranceAnimation";
import { recordHasRight, HIGHLIGHT_STYLE, HIGHLIGHT_ROW_BG } from "@/utils";
import { useEntityCommentsDrawer } from "@/components/features/Comments";

import { TaskProgress } from "../TaskProgress/TaskProgress";
import {
  formatScheduledAt,
  formatTaskLocation,
  buildTaskMapsUrl,
  taskOnlineUrl,
  getLocationKindIcon,
  getResponsibleKindIcon,
  getTaskTypeLabel,
  isTaskDone,
} from "../taskHelpers";

// ============================================
// Props
// ============================================

export interface TasksListProps {
  data?: Task[];
  loading?: boolean;
  loadingMore?: boolean;
  hasMore?: boolean;
  onLoadMore?: () => void;
  onEditTask?: (task: Task) => void;
  onDeleteTask?: (task: Task) => void;
  onTransition?: (task: Task, action: TaskTransitionAction) => void;
  isRemoving?: (id?: string) => boolean;
  isTransitioning?: (id?: string) => boolean;
  highlightedId?: string | null;
  onAddTask?: () => void;
  eventTimezone?: string | null;
  selection?: RowSelection;
  selectable?: boolean;
  /**
   * A search term or filter is narrowing the list. An empty result then means "no match" — NOT
   * "nothing here yet", so the first-run Add prompt would be telling somebody who just searched
   * that their records are gone. ⚠ The list cannot see the page's search box; this is a PROP.
   */
  hasQueryNarrowing?: boolean;
}

// ============================================
// Shared bits
// ============================================

const isDisabled = (task: Task): boolean => task.status === STATUSES.DISABLED;

interface CategoryMeta {
  name: string | null;
  icon: string | null;
  color: string | null;
}

const typeIcon = (task: Task): string =>
  task.taskType === "appointment" ? "CalendarClock" : "Checklist";

const StatusBadge: React.FC<{ task: Task }> = ({ task }) => {
  if (!isDisabled(task)) {
    return null;
  }

  return (
    <Badge size="xs" variant="light" color="gray">
      Disabled
    </Badge>
  );
};

const CategoryCell: React.FC<{ meta: CategoryMeta | null }> = ({ meta }) => {
  if (!meta?.name) {
    return (
      <Text size="sm" c="dimmed">
        —
      </Text>
    );
  }

  return (
    <Group gap={6} wrap="nowrap">
      {meta.icon ? <AppIcon iconName={meta.icon} size={16} color={meta.color || undefined} /> : null}
      <Text size="md" c={meta.color || undefined} lineClamp={1}>
        {meta.name}
      </Text>
    </Group>
  );
};

const LocationLine: React.FC<{ task: Task; location: string; href: string | null; compact?: boolean }> = ({
  task,
  location,
  href,
  compact,
}) => (
  <Group gap={compact ? 4 : 6} wrap="nowrap" c="dimmed" mt={2}>
    <AppIcon iconName={getLocationKindIcon(task.locationKind)} size={compact ? 12 : 13} />
    {href ? (
      <Anchor
        href={href}
        target="_blank"
        rel="noopener noreferrer"
        onClick={(e) => e.stopPropagation()}
        size={compact ? "xs" : "sm"}
        lineClamp={1}
      >
        {location}
      </Anchor>
    ) : (
      <Text size={compact ? "xs" : "sm"} lineClamp={1}>
        {location}
      </Text>
    )}
  </Group>
);

const ResponsibleCell: React.FC<{ task: Task }> = ({ task }) => {
  if (!task.responsibleLabel) {
    return (
      <Text size="sm" c="dimmed">
        —
      </Text>
    );
  }

  return (
    <Group gap={6} wrap="nowrap">
      <AppIcon iconName={getResponsibleKindIcon(task.responsibleKind)} size={15} />
      <Text size="md" lineClamp={1}>
        {task.responsibleLabel}
      </Text>
    </Group>
  );
};

const TaskIcon: React.FC<{ task: Task; meta: CategoryMeta | null; size?: number }> = ({ task, meta, size = 34 }) => (
  <ThemeIcon variant="light" color={meta?.color || (isTaskDone(task) ? "green" : "blue")} radius="xl" size={size}>
    <AppIcon iconName={meta?.icon || typeIcon(task)} size={Math.round(size * 0.55)} />
  </ThemeIcon>
);

const RowActions: React.FC<{
  task: Task;
  onEdit?: (t: Task) => void;
  onDelete?: (t: Task) => void;
  onOpenComments?: (t: Task) => void;
  isDeleting: boolean;
}> = ({ task, onEdit, onDelete, onOpenComments, isDeleting }) => {
  const showEdit = !!onEdit && recordHasRight(task, "edit");
  const showDelete = !!onDelete && recordHasRight(task, "remove");
  const showComments = !!onOpenComments; // comments are open to any viewer of the item

  if (!showEdit && !showDelete && !showComments) {
    return null;
  }

  return (
    <Group gap={4} wrap="nowrap">
      {showComments && onOpenComments && (
        <Tooltip label="Comments" withArrow position="bottom">
          <ActionIcon
            variant="subtle"
            color="gray"
            size="md"
            onClick={() => onOpenComments(task)}
            disabled={isDeleting}
            aria-label="Comments"
          >
            <AppIcon iconName="Message" size={16} />
          </ActionIcon>
        </Tooltip>
      )}
      {showEdit && onEdit && (
        <Tooltip label="Edit" withArrow position="bottom">
          <ActionIcon variant="subtle" color="blue" size="md" onClick={() => onEdit(task)} disabled={isDeleting} aria-label="Edit">
            <AppIcon iconName="Edit" size={16} />
          </ActionIcon>
        </Tooltip>
      )}
      {showDelete && onDelete && (
        <Tooltip label="Delete" withArrow position="bottom">
          <ActionIcon
            variant="subtle"
            color="red"
            size="md"
            onClick={() => onDelete(task)}
            loading={isDeleting}
            disabled={isDeleting}
            aria-label="Delete"
          >
            <AppIcon iconName="Trash" size={16} />
          </ActionIcon>
        </Tooltip>
      )}
    </Group>
  );
};

// ============================================
// Row shape
// ============================================

interface RowProps {
  task: Task;
  title: string;
  meta: CategoryMeta | null;
  scheduled: string | null;
  location: string | null;
  locationHref: string | null;
  disabled: boolean;
  onEdit?: (t: Task) => void;
  onDelete?: (t: Task) => void;
  onOpenComments?: (t: Task) => void;
  onTransition: (action: TaskTransitionAction) => void;
  transitioning: boolean;
  highlighted: boolean;
  isDeleting: boolean;
  selectionColumn: boolean;
  selectable: boolean;
  selected: boolean;
  selectionMode: boolean;
  onToggleSelect?: (id: string) => void;
}

// ============================================
// Desktop table row
// ============================================

const TaskTableRow: React.FC<RowProps> = ({
  task,
  title,
  meta,
  scheduled,
  location,
  locationHref,
  disabled,
  onEdit,
  onDelete,
  onOpenComments,
  onTransition,
  transitioning,
  highlighted,
  isDeleting,
  selectionColumn,
  selectable,
  selected,
  onToggleSelect,
}) => {
  return (
    <Table.Tr
      style={{
        opacity: isDeleting ? 0.5 : disabled ? 0.6 : 1,
        backgroundColor: highlighted ? HIGHLIGHT_ROW_BG : selected ? "var(--mantine-color-blue-light)" : undefined,
        transition: "background-color 150ms ease, opacity 150ms ease",
      }}
    >
      {selectionColumn && (
        <Table.Td style={{ width: 44 }}>
          {selectable && (
            <Checkbox checked={selected} onChange={() => task.id && onToggleSelect?.(task.id)} disabled={isDeleting} aria-label={title} />
          )}
        </Table.Td>
      )}
      <Table.Td>
        <Group gap="sm" wrap="nowrap">
          <TaskIcon task={task} meta={meta} />
          <Box style={{ minWidth: 0 }}>
            <Group gap={6} wrap="nowrap">
              <Text size="md" fw={600} lineClamp={1}>
                {title}
              </Text>
              <StatusBadge task={task} />
            </Group>
            <Text size="xs" c="dimmed">
              {getTaskTypeLabel(task.taskType)}
            </Text>
          </Box>
        </Group>
      </Table.Td>
      <Table.Td style={{ width: 170 }}>
        <CategoryCell meta={meta} />
      </Table.Td>
      <Table.Td style={{ width: 150 }}>
        <Text size="md" c={scheduled ? undefined : "dimmed"} lineClamp={1}>
          {scheduled || "—"}
        </Text>
        {location ? <LocationLine task={task} location={location} href={locationHref} compact /> : null}
      </Table.Td>
      <Table.Td style={{ width: 170 }}>
        <ResponsibleCell task={task} />
      </Table.Td>
      <Table.Td style={{ width: 300 }}>
        <TaskProgress task={task} onTransition={onTransition} transitioning={transitioning} compact />
      </Table.Td>
      <Table.Td style={{ width: 90 }}>
        <RowActions task={task} onEdit={onEdit} onDelete={onDelete} onOpenComments={onOpenComments} isDeleting={isDeleting} />
      </Table.Td>
    </Table.Tr>
  );
};

const TaskTableRowSkeleton: React.FC<{ selectionColumn?: boolean }> = ({ selectionColumn }) => (
  <Table.Tr>
    {selectionColumn && (
      <Table.Td style={{ width: 44 }}>
        <Skeleton height={20} width={20} radius="sm" />
      </Table.Td>
    )}
    <Table.Td>
      <Group gap="sm" wrap="nowrap">
        <Skeleton circle height={34} width={34} />
        <Skeleton height={14} width="40%" maw={200} />
      </Group>
    </Table.Td>
    <Table.Td style={{ width: 170 }}>
      <Skeleton height={14} width={120} />
    </Table.Td>
    <Table.Td style={{ width: 150 }}>
      <Skeleton height={14} width={110} />
    </Table.Td>
    <Table.Td style={{ width: 170 }}>
      <Skeleton height={14} width={120} />
    </Table.Td>
    <Table.Td style={{ width: 300 }}>
      <Skeleton height={26} width={240} />
    </Table.Td>
    <Table.Td style={{ width: 90 }}>
      <Group gap={4} wrap="nowrap">
        <Skeleton circle height={28} width={28} />
        <Skeleton circle height={28} width={28} />
      </Group>
    </Table.Td>
  </Table.Tr>
);

// ============================================
// Card (tablet / mobile)
// ============================================

interface CardProps extends RowProps {
  forceClose?: boolean;
  onForceCloseComplete?: () => void;
  onSwipeStateChange?: (isOpen: boolean) => void;
}

const TaskCardComponent: React.FC<CardProps> = ({
  task,
  title,
  meta,
  scheduled,
  location,
  locationHref,
  disabled,
  onEdit,
  onDelete,
  onOpenComments,
  onTransition,
  transitioning,
  highlighted,
  isDeleting,
  selectable,
  selected,
  selectionMode,
  onToggleSelect,
  forceClose,
  onForceCloseComplete,
  onSwipeStateChange,
}) => {
  const toggleSelect = () => task.id && onToggleSelect?.(task.id);

  const swipeActions = useMemo((): SwipeAction[] => {
    const actions: SwipeAction[] = [];

    // Comments is a secondary action (auto-routes to the LEFT swipe side); edit/remove stay right.
    if (onOpenComments) {
      actions.push({
        key: "comments",
        icon: "Message",
        label: "Comments",
        color: "gray",
        onClick: () => onOpenComments(task),
        disabled: isDeleting,
        secondary: true,
      });
    }

    if (onEdit && recordHasRight(task, "edit")) {
      actions.push({ key: "edit", icon: "Edit", label: "Edit", color: "blue", onClick: () => onEdit(task), disabled: isDeleting });
    }

    if (onDelete && recordHasRight(task, "remove")) {
      actions.push({
        key: "delete",
        icon: "Trash",
        label: "Delete",
        color: "red",
        onClick: () => onDelete(task),
        loading: isDeleting,
        disabled: isDeleting,
      });
    }

    return actions;
  }, [onEdit, onDelete, onOpenComments, task, isDeleting]);

  const cardBody = (showActions: boolean, showCheckbox: boolean) => (
    <Paper
      p="md"
      withBorder
      radius="md"
      opacity={isDeleting ? 0.5 : disabled ? 0.6 : 1}
      style={{
        transition: "opacity 150ms ease, box-shadow 150ms ease",
        backgroundColor: selected ? "var(--mantine-color-blue-light)" : undefined,
        ...(highlighted ? HIGHLIGHT_STYLE : {}),
      }}
    >
      <Group gap="md" wrap="nowrap" align="flex-start">
        {showCheckbox && (
          <Checkbox mt={2} checked={selected} onChange={toggleSelect} onClick={(e) => e.stopPropagation()} disabled={isDeleting} aria-label={title} />
        )}
        <TaskIcon task={task} meta={meta} size={40} />
        <Box style={{ flex: 1, minWidth: 0 }}>
          <Group gap={6} wrap="nowrap">
            <Text size="md" fw={600} lineClamp={1}>
              {title}
            </Text>
            <StatusBadge task={task} />
          </Group>
          <Text size="xs" c="dimmed">
            {getTaskTypeLabel(task.taskType)}
          </Text>
          <Box mt={4}>
            <CategoryCell meta={meta} />
          </Box>
          {scheduled && (
            <Group gap={6} wrap="nowrap" mt={2} c="dimmed">
              <AppIcon iconName="Clock" size={13} />
              <Text size="md">{scheduled}</Text>
            </Group>
          )}
          {task.responsibleLabel && (
            <Box mt={2}>
              <ResponsibleCell task={task} />
            </Box>
          )}
          {location && <LocationLine task={task} location={location} href={locationHref} />}
          <Box mt="xs">
            <TaskProgress task={task} onTransition={onTransition} transitioning={transitioning} compact />
          </Box>
        </Box>
        {showActions && <RowActions task={task} onEdit={onEdit} onDelete={onDelete} onOpenComments={onOpenComments} isDeleting={isDeleting} />}
      </Group>
    </Paper>
  );

  if (selectionMode) {
    return (
      <>
        <Box hiddenFrom="sm" onClick={selectable ? toggleSelect : undefined} style={{ cursor: selectable ? "pointer" : "default", userSelect: "none" }}>
          {cardBody(false, selectable)}
        </Box>
        <Box visibleFrom="sm">{cardBody(true, selectable)}</Box>
      </>
    );
  }

  return (
    <>
      <Box hiddenFrom="sm">
        {swipeActions.length > 0 ? (
          <SwipeableCard
            leftSwipeActions={swipeActions}
            forceClose={forceClose}
            onForceCloseComplete={onForceCloseComplete}
            onSwipeStateChange={onSwipeStateChange}
            disabled={isDeleting}
            borderRadius="var(--mantine-radius-md)"
          >
            {cardBody(false, false)}
          </SwipeableCard>
        ) : (
          cardBody(false, false)
        )}
      </Box>
      <Box visibleFrom="sm">{cardBody(true, selectable)}</Box>
    </>
  );
};

const TaskCard = React.memo(TaskCardComponent);

const TaskCardSkeleton: React.FC<CardSkeletonShape> = ({ actions, withCheckbox }) => (
  <Paper p="md" withBorder radius="md">
    <Group gap="md" wrap="nowrap" align="flex-start">
      <CardSkeletonCheckbox show={withCheckbox} />
      <Skeleton circle height={40} width={40} />
      <CardSkeletonLines count={6} sizes={["md", "xs", "sm", "md", "md", "sm"]} gap={3} />
      <CardSkeletonActions count={actions} />
    </Group>
  </Paper>
);

// ============================================
// Main
// ============================================

const TasksListComponent: React.FC<TasksListProps> = ({
  data: dataProp,
  loading: loadingProp,
  loadingMore: loadingMoreProp,
  hasMore: hasMoreProp,
  onLoadMore: onLoadMoreProp,
  onEditTask,
  onDeleteTask,
  onTransition: onTransitionProp,
  isRemoving: isRemovingProp,
  isTransitioning: isTransitioningProp,
  highlightedId,
  onAddTask,
  eventTimezone,
  selection,
  selectable = false,
  hasQueryNarrowing = false,
}) => {
  const { isDesktop } = useAppEnvironment();

  const isSelectable = selectable && !!selection;
  // The tablet card's row buttons, one per handler — what the card skeleton draws (▶ check-card-skeletons).
  const cardActions = countCardActions(onEditTask, onDeleteTask, true /* comments — open to every viewer */);

  const tasksContext = useOptionalTasksContext();
  const categoriesContext = useOptionalCategoriesContext();

  const tasks = dataProp ?? tasksContext?.tasks ?? [];
  const loading = loadingProp ?? tasksContext?.loading ?? false;
  const loadingMore = loadingMoreProp ?? tasksContext?.loadingMore ?? false;
  const hasMore = hasMoreProp ?? tasksContext?.hasMore ?? false;
  const onLoadMore = onLoadMoreProp ?? tasksContext?.loadMore;
  const isRemoving = isRemovingProp ?? tasksContext?.isRemoving ?? (() => false);
  const isTransitioning = isTransitioningProp ?? tasksContext?.isTransitioning ?? (() => false);
  const transitionFn = tasksContext?.transition;

  const onTransition = useCallback(
    (task: Task, action: TaskTransitionAction) => {
      if (onTransitionProp) {
        onTransitionProp(task, action);
      } else if (task.id && transitionFn) {
        transitionFn(task.id, action);
      }
    },
    [onTransitionProp, transitionFn]
  );

  const { openCard, closeAllCards, shouldForceClose, handleForceCloseComplete } = useSwipeableList();
  const { hasAnimated } = useEntranceAnimation({ itemCount: tasks.length });

  // Comments row action — opens the reusable drawer for any viewer, without the edit drawer. Mounted once
  // (below) via `comments.drawer`; each row calls `comments.open(...)` from its Comments action.
  const comments = useEntityCommentsDrawer();

  const categoryMeta = useCallback(
    (task: Task): CategoryMeta | null => {
      if (!task.categoryId) {
        return null;
      }

      const category = categoriesContext?.getById(task.categoryId);

      if (!category) {
        return null;
      }

      return { name: category.name || category.code || null, icon: category.icon ?? null, color: category.color ?? null };
    },
    [categoriesContext]
  );

  const handleEdit = useCallback(
    (task: Task) => {
      closeAllCards();
      onEditTask?.(task);
    },
    [onEditTask, closeAllCards]
  );

  const handleDelete = useCallback(
    (task: Task) => {
      closeAllCards();
      onDeleteTask?.(task);
    },
    [onDeleteTask, closeAllCards]
  );

  const handleSwipeStateChange = useCallback(
    (id: string, isOpen: boolean) => {
      if (isOpen) {
        openCard(id);
      }
    },
    [openCard]
  );

  // ⚠ A SKELETON IS FOR THE FIRST READ ONLY. `loading && x.length === 0` re-shows it on every
  // refetch of an EMPTY list, so an empty page reads skeleton → "none" → skeleton → "none". Once
  // the first read is back, a refetch keeps the rows OR the empty state and ContentLoadingOverlay
  // says it is reloading. ⚠ The fallback answers "is this all the data there will ever be?" for a
  // caller with no provider. ▶ npm run check-list-states
  const isLoaded = tasksContext?.isLoaded ?? dataProp !== undefined;
  const showSkeleton = !isLoaded;

  if (!showSkeleton && tasks.length === 0) {
    if (hasQueryNarrowing) {
      return (
        <Text size="md" c="dimmed" ta="center" p="md">
          No tasks match your search or filters.
        </Text>
      );
    }

    return (
      <Paper withBorder p="xl" radius="md">
        <EmptyState
          title="No tasks yet"
          description="Add the things to do before, during and after your event — with a schedule, an owner, and time tracking."
          iconName="Checklist"
          actionLabel="Add task"
          onAction={onAddTask}
          size="md"
        />
      </Paper>
    );
  }

  const rowIsSelectable = (task: Task): boolean =>
    isSelectable && ((!!onEditTask && recordHasRight(task, "edit")) || (!!onDeleteTask && recordHasRight(task, "remove")));

  const rowProps = (task: Task): RowProps => ({
    task,
    title: task.title || "—",
    meta: categoryMeta(task),
    scheduled: formatScheduledAt(task.scheduledAt, eventTimezone),
    location: formatTaskLocation(task),
    locationHref: buildTaskMapsUrl(task) ?? taskOnlineUrl(task),
    disabled: isDisabled(task),
    onEdit: onEditTask ? handleEdit : undefined,
    onDelete: onDeleteTask ? handleDelete : undefined,
    onOpenComments: (t) =>
      comments.open({ eventId: t.eventId ?? "", entityKind: "task", entityId: t.id ?? "", title: t.title ?? undefined }),
    onTransition: (action) => onTransition(task, action),
    transitioning: isTransitioning(task.id),
    highlighted: task.id === highlightedId,
    isDeleting: isRemoving(task.id),
    selectionColumn: isSelectable,
    selectable: rowIsSelectable(task),
    selected: selection?.isSelected(task.id) ?? false,
    selectionMode: selection?.selectionMode ?? false,
    onToggleSelect: selection?.toggle,
  });

  const selectableVisibleIds = tasks
    .filter((task) => rowIsSelectable(task))
    .map((task) => task.id)
    .filter((id): id is string => !!id);

  const loadMore = <LoadMoreButton hasMore={hasMore} loading={loading} loadingMore={loadingMore} onLoadMore={onLoadMore} />;

  // ── Desktop table ──
  if (isDesktop) {
    return (
      <Stack gap="md">
        <Paper withBorder radius="md" style={{ overflow: "hidden" }}>
          <Table verticalSpacing="sm" horizontalSpacing="md">
            <Table.Thead>
              <Table.Tr>
                {isSelectable && (
                  <Table.Th style={{ width: 44 }}>
                    <Checkbox
                      checked={selection!.allSelected(selectableVisibleIds)}
                      indeterminate={selection!.someSelected(selectableVisibleIds)}
                      onChange={() => selection!.toggleAll(selectableVisibleIds)}
                      disabled={selectableVisibleIds.length === 0}
                      aria-label="Select all"
                    />
                  </Table.Th>
                )}
                <Table.Th>Task</Table.Th>
                <Table.Th style={{ width: 170 }}>Category</Table.Th>
                <Table.Th style={{ width: 150 }}>When</Table.Th>
                <Table.Th style={{ width: 170 }}>Responsible</Table.Th>
                <Table.Th style={{ width: 300 }}>Progress</Table.Th>
                <Table.Th style={{ width: 90 }} />
              </Table.Tr>
            </Table.Thead>
            <Table.Tbody>
              {showSkeleton
                ? Array.from({ length: 8 }).map((_, i) => <TaskTableRowSkeleton key={i} selectionColumn={isSelectable} />)
                : tasks.map((task) => <TaskTableRow key={task.id} {...rowProps(task)} />)}
            </Table.Tbody>
          </Table>
        </Paper>
        {!showSkeleton && loadMore}
        {comments.drawer}
      </Stack>
    );
  }

  // ── Tablet / mobile cards ──
  if (showSkeleton) {
    return (
      <Stack gap="sm">
        {Array.from({ length: 6 }).map((_, i) => (
          <TaskCardSkeleton key={i} actions={cardActions} withCheckbox={isSelectable} />
        ))}
      </Stack>
    );
  }

  return (
    <Stack gap="md">
      <motion.div
        variants={hasAnimated ? undefined : entranceListVariants}
        initial={hasAnimated ? false : "hidden"}
        animate={hasAnimated ? undefined : "visible"}
        style={{ display: "flex", flexDirection: "column", gap: "var(--mantine-spacing-sm)" }}
      >
        <AnimatePresence mode="sync">
          {tasks.map((task) => (
            <motion.div
              key={task.id}
              variants={hasAnimated ? undefined : entranceCardVariants}
              initial={hasAnimated ? false : "hidden"}
              animate={hasAnimated ? undefined : "visible"}
              exit="exit"
              layout
            >
              <TaskCard
                {...rowProps(task)}
                forceClose={shouldForceClose(task.id || "")}
                onForceCloseComplete={() => handleForceCloseComplete(task.id || "")}
                onSwipeStateChange={(isOpen) => handleSwipeStateChange(task.id || "", isOpen)}
              />
            </motion.div>
          ))}
        </AnimatePresence>
      </motion.div>
      {loadMore}
      {comments.drawer}
    </Stack>
  );
};

export const TasksList = React.memo(TasksListComponent);
